// Lista de Provas Agendadas do aluno — futuras, em andamento e passadas.
// Acessada via menu "Provas" do aluno. Cada card abre o ScheduledExamWorkspace
// pela URL /student/scheduled-exams/:examId.
//
// Estrutura visual:
//   Em andamento agora  (running)   — destaque pulsante (vermelho/primary)
//   Próximas             (lobby)     — agendadas, ainda não começaram
//   Encerradas           (closed)    — terminaram, sem release ainda (aguarda gabarito)
//   Resultados disponíveis (released) — gabarito liberado, abre em modo consulta

import { signal } from "@preact/signals";
import { useEffect, useMemo, useState } from "preact/hooks";
import {
  AlertCircle,
  Calendar,
  CheckCircle2,
  ChevronRight,
  Clock,
  FileText,
  Flag,
  Hourglass,
  Loader2,
  Play,
  Timer
} from "lucide-react";
import { Skeleton } from "@app/ui/components/skeleton";
import { api, ApiError, type StudentExamRow } from "./api";
import { enterScheduledExamView } from "./ScheduledExamWorkspace";

const exams = signal<StudentExamRow[] | null>(null);
const phase = signal<"loading" | "ready" | "error">("loading");
const errMsg = signal<string | null>(null);
// Tick pra atualizar countdown de "começa em X" sem precisar refazer fetch.
const _listTick = signal<number>(0);

async function load() {
  phase.value = "loading";
  errMsg.value = null;
  try {
    const r = await api.listScheduledExams();
    exams.value = r.exams;
    phase.value = "ready";
  } catch (err) {
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    errMsg.value = err instanceof Error ? err.message : String(err);
    phase.value = "error";
  }
}

export function MinhasProvasView() {
  useEffect(() => {
    void load();
  }, []);
  // Tick a cada 30s pra atualizar countdowns (não precisa de mais resolução).
  useEffect(() => {
    const t = setInterval(() => {
      _listTick.value = _listTick.value + 1;
    }, 30_000);
    return () => clearInterval(t);
  }, []);
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  _listTick.value;

  if (phase.value === "loading") return <ProvasLoading />;
  if (phase.value === "error") return <ProvasError msg={errMsg.value} />;

  const all = exams.value ?? [];
  const running = all.filter((e) => e.phase === "running");
  const lobby = all.filter((e) => e.phase === "lobby");
  const released = all.filter((e) => e.phase === "released");
  const closed = all.filter((e) => e.phase === "closed");

  if (all.length === 0) {
    return <ProvasEmpty />;
  }

  return (
    <div class="space-y-6">
      <header class="space-y-1">
        <h1 class="text-xl font-bold">Minhas provas</h1>
        <p class="text-sm text-muted-foreground">
          Provas agendadas pelos seus professores. Você participa de acordo
          com a turma em que está matriculado.
        </p>
      </header>

      {running.length > 0 && (
        <Section
          title="Em curso agora"
          accent="amber"
          icon={<Play className="size-4" />}
          count={running.length}
        >
          {running.map((e) => (
            <ExamCard key={e.id} exam={e} variant="running" />
          ))}
        </Section>
      )}

      {lobby.length > 0 && (
        <Section
          title="Próximas"
          accent="primary"
          icon={<Calendar className="size-4" />}
          count={lobby.length}
        >
          {lobby.map((e) => (
            <ExamCard key={e.id} exam={e} variant="lobby" />
          ))}
        </Section>
      )}

      {released.length > 0 && (
        <Section
          title="Resultados disponíveis"
          accent="violet"
          icon={<CheckCircle2 className="size-4" />}
          count={released.length}
        >
          {released.map((e) => (
            <ExamCard key={e.id} exam={e} variant="released" />
          ))}
        </Section>
      )}

      {closed.length > 0 && (
        <Section
          title="Aguardando gabarito"
          accent="muted"
          icon={<Hourglass className="size-4" />}
          count={closed.length}
        >
          {closed.map((e) => (
            <ExamCard key={e.id} exam={e} variant="closed" />
          ))}
        </Section>
      )}
    </div>
  );
}

// =====================
// Section + Card
// =====================

function Section({
  title,
  accent,
  icon,
  count,
  children
}: {
  title: string;
  accent: "primary" | "emerald" | "violet" | "amber" | "muted";
  icon: any;
  count: number;
  children: any;
}) {
  const accentCls =
    accent === "emerald"
      ? "text-emerald-700 dark:text-emerald-400"
      : accent === "violet"
        ? "text-violet-600 dark:text-violet-400"
        : accent === "amber"
          ? "text-amber-700 dark:text-amber-400"
          : accent === "muted"
            ? "text-muted-foreground"
            : "text-primary";
  return (
    <section class="space-y-3">
      <h2 class={`flex items-center gap-2 text-xs font-bold uppercase tracking-wider ${accentCls}`}>
        {icon}
        {title}
        <span class="rounded-md bg-background px-1.5 py-0.5 text-[10px] tabular-nums text-muted-foreground">
          {count}
        </span>
      </h2>
      <div class="space-y-2">{children}</div>
    </section>
  );
}

function ExamCard({
  exam,
  variant
}: {
  exam: StudentExamRow;
  variant: "running" | "lobby" | "released" | "closed";
}) {
  const now = Date.now();
  const startsAtMs = exam.startsAt ? Date.parse(exam.startsAt) : null;
  const endsAtMs = exam.endsAt ? Date.parse(exam.endsAt) : null;
  const restoStart = startsAtMs ? Math.max(0, startsAtMs - now) : null;
  const restoEnd = endsAtMs ? Math.max(0, endsAtMs - now) : null;

  const accentBorder =
    variant === "running"
      ? "border-amber-500/50 hover:border-amber-500/80 bg-amber-500/10"
      : variant === "lobby"
        ? "border-primary/30 hover:border-primary/60 bg-primary/5"
        : variant === "released"
          ? "border-violet-500/30 hover:border-violet-500/60 bg-violet-500/5"
          : "border-border hover:border-muted-foreground/40 bg-muted/30";

  const accentIcon =
    variant === "running" ? (
      <span class="relative grid size-10 shrink-0 place-items-center rounded-md bg-amber-500 text-white">
        <Play className="size-5" strokeWidth={2.5} />
        <span class="absolute -top-0.5 -right-0.5 size-2.5 animate-pulse rounded-full bg-amber-300 ring-2 ring-background" />
      </span>
    ) : variant === "lobby" ? (
      <span class="grid size-10 shrink-0 place-items-center rounded-md bg-primary text-primary-foreground">
        <Calendar className="size-5" />
      </span>
    ) : variant === "released" ? (
      <span class="grid size-10 shrink-0 place-items-center rounded-md bg-violet-500 text-white">
        <Flag className="size-5" />
      </span>
    ) : (
      <span class="grid size-10 shrink-0 place-items-center rounded-md bg-muted-foreground/30 text-foreground">
        <Hourglass className="size-5" />
      </span>
    );

  return (
    <button
      type="button"
      onClick={() => enterScheduledExamView(exam.id)}
      class={`group flex w-full items-center gap-3 rounded-md border px-4 py-3 text-left transition-colors ${accentBorder}`}
    >
      {accentIcon}
      <div class="min-w-0 flex-1 space-y-1">
        <div class="flex items-center gap-2">
          <h3 class="truncate text-sm font-bold">{exam.title}</h3>
          {exam.attempt?.score && variant === "released" && (
            <span class="inline-flex items-center gap-1 rounded-md bg-violet-500/15 px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-violet-700 dark:text-violet-300">
              Nota {Number(exam.attempt.score).toFixed(2)}
            </span>
          )}
        </div>
        <div class="truncate text-xs text-muted-foreground">
          {exam.disciplinaName}
        </div>
        <div class="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
          {exam.startsAt && (
            <span class="inline-flex items-center gap-1">
              <Clock className="size-3" />
              {new Date(exam.startsAt).toLocaleString("pt-BR", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit"
              })}
            </span>
          )}
          {exam.durationMinutes != null && (
            <span class="inline-flex items-center gap-1">
              <Timer className="size-3" />
              {exam.durationMinutes} min
            </span>
          )}
          {variant === "lobby" && restoStart !== null && (
            <span class="inline-flex items-center gap-1 rounded-md bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-primary">
              Começa em {humanFromMs(restoStart)}
            </span>
          )}
          {variant === "running" && restoEnd !== null && (
            <span class="inline-flex items-center gap-1 rounded-md bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-emerald-700 dark:text-emerald-300">
              Restam {humanFromMs(restoEnd)}
            </span>
          )}
          {variant === "released" && exam.attempt?.submittedAt && (
            <span class="inline-flex items-center gap-1">
              <CheckCircle2 className="size-3 text-violet-500" />
              Submetida
            </span>
          )}
          {variant === "closed" && (
            <span class="inline-flex items-center gap-1 text-amber-700 dark:text-amber-400">
              Aguardando liberação do gabarito
            </span>
          )}
        </div>
      </div>
      <ChevronRight className="size-4 shrink-0 text-muted-foreground/70 transition-transform group-hover:translate-x-0.5" />
    </button>
  );
}

function humanFromMs(ms: number): string {
  const sec = Math.floor(ms / 1000);
  const days = Math.floor(sec / 86400);
  if (days > 1) return `${days} dias`;
  const hours = Math.floor(sec / 3600);
  if (hours > 1) return `${hours}h`;
  const mins = Math.floor(sec / 60);
  if (mins > 1) return `${mins} min`;
  return `${Math.max(0, sec)}s`;
}

// =====================
// States
// =====================

function ProvasLoading() {
  return (
    <div class="space-y-4">
      <Skeleton className="h-8 w-40 rounded-md" />
      <Skeleton className="h-20 w-full rounded-md" />
      <Skeleton className="h-20 w-full rounded-md" />
      <Skeleton className="h-20 w-full rounded-md" />
    </div>
  );
}

function ProvasError({ msg }: { msg: string | null }) {
  return (
    <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
      <AlertCircle className="mr-2 inline size-4 text-destructive" />
      {msg ?? "Erro ao carregar provas."}
    </div>
  );
}

function ProvasEmpty() {
  return (
    <div class="space-y-4">
      <header class="space-y-1">
        <h1 class="text-xl font-bold">Minhas provas</h1>
      </header>
      <div class="rounded-md border border-dashed border-border bg-card/40 px-6 py-12 text-center">
        <FileText className="mx-auto size-10 text-muted-foreground/60" />
        <h2 class="mt-3 text-sm font-bold">Nenhuma prova agendada</h2>
        <p class="mt-1 text-xs text-muted-foreground">
          Quando seus professores agendarem provas pra suas turmas, elas
          aparecem aqui.
        </p>
      </div>
    </div>
  );
}
